import type { ReactNode } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { GA4_GROUP_BY_LABELS, type Ga4TrafficReport } from '@/api/types'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, LoadingBlock } from '@/design'
import { trafficKeys } from '@/features/ga4-shared/keys'
import { usePermissions } from '@/lib/access'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'
import type { GlobalFilters } from '@/lib/filters'

import { CsvButton } from './CsvButton'
import { csvFilename } from './download'
import { exportErrorMessage } from './errors'
import { FirstPartyTraffic } from './FirstPartyTraffic'
import { useTrafficSource } from './firstPartyQuery'
import { ComparisonNote, GroupBySelect, SourceSwitch } from './TrafficControls'
import { NoProperty, NoRows, TrafficError } from './TrafficStates'
import { SourceBadge, TrafficFreshness } from './TrafficBadges'
import { TrafficTable, TrafficTotals } from './TrafficTable'
import { subtitleFor } from './metrics'
import { useGroupBy, useTrafficQuery } from './trafficQuery'
import './traffic.css'

/**
 * Analytics › Website: where the website's traffic comes from, from one of two
 * sources the switch chooses (`tr_source`) — never both in one table:
 *
 * - **Google Analytics 4** (the default): sessions, users, conversions and revenue
 *   as GA4 reports them, by channel, source / medium, campaign, landing page, device
 *   or country, under the GA4 badge.
 * - **TmT first-party**: the website's own consented visits and orders, drilled down
 *   channel → landing page under a chosen attribution model (`FirstPartyTraffic`).
 */
export function TrafficPage({ route }: { route: RouteDef }) {
  const { filters } = useGlobalFilters()
  const [source, setSource] = useTrafficSource()
  const sourceSwitch = <SourceSwitch value={source} onChange={setSource} />
  if (source === 'tmt') return <FirstPartyTraffic route={route} filters={filters} sourceSwitch={sourceSwitch} />
  return <Ga4Traffic route={route} filters={filters} sourceSwitch={sourceSwitch} />
}

/** Every number here is GA4's; the badge, its caveat and the freshness line say so (blueprint 06 §6.10). */
function Ga4Traffic({ route, filters, sourceSwitch }: { route: RouteDef; filters: GlobalFilters; sourceSwitch: ReactNode }) {
  const client = useApiClient()
  const { can } = usePermissions()
  const [groupBy, setGroupBy] = useGroupBy()
  const query = useTrafficQuery(filters, groupBy)
  const traffic = useQuery({ queryKey: trafficKeys.report(client.kind, query), queryFn: () => client.traffic(query) })
  const source = client.kind === 'http' ? 'live' : 'demo'
  usePageFreshness(traffic.data ? { source, lastUpdatedAt: traffic.data.freshness.lastImportAt } : undefined)

  const report = traffic.data

  return (
    <SectionFrame route={route} actions={<SourceBadge />}>
      <div className="ci-stack">
        <div className="tr-controls">
          {sourceSwitch}
          <GroupBySelect value={groupBy} onChange={setGroupBy} />
          {report ? <TrafficFreshness freshness={report.freshness} source={source} /> : null}
          {can('exports.create') && report && report.rows.length > 0 ? (
            <CsvButton fetchCsv={() => client.trafficCsv(query)} filename={csvFilename(query)} errorMessage={exportErrorMessage} />
          ) : null}
        </div>
        <ComparisonNote comparison={filters.compare} />

        <TrafficBody traffic={traffic} />
      </div>
    </SectionFrame>
  )
}

/** Exactly one of: loading, error, nothing connected, nothing in range, or the results. */
function TrafficBody({ traffic }: { traffic: UseQueryResult<Ga4TrafficReport> }) {
  const report = traffic.data
  if (traffic.isPending) return <LoadingBlock rows={6} label="Loading Google Analytics traffic" />
  if (!report) return <TrafficError error={traffic.error} />
  if (report.properties.length === 0) return <NoProperty />
  if (report.rows.length === 0) return <NoRows from={report.start} to={report.end} />
  return (
    <Card title={`By ${GA4_GROUP_BY_LABELS[report.groupBy].toLowerCase()}`} subtitle={subtitleFor(report)}>
      <TrafficTotals report={report} />
      <TrafficTable report={report} />
    </Card>
  )
}
